import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import SnakeGame from "./SnakeGame";
import TutorialSlideshow from "../TutorialSlideshow/TutorialSlideshow";
import { notify } from "../../utils/notify.jsx";

export default function LoadingOverlay({ states, onComplete = () => {}, loadingFor = null, canGoToDesign = false, goToDesignRoute = null, goToWorldRoute = null, canCancel = false, onCancel = null, draftedResult = null, defaultMinimized = false, showTutorial = true }) {
  const [currentState, setCurrentState] = useState(0);
  const [actualLoadingMessage, setActualLoadingMessage] = useState('');
  const [showGame, setShowGame] = useState(false);
  const [expandedPreview, setExpandedPreview] = useState(false);
  const [isMinimized, setIsMinimized] = useState(defaultMinimized);
  const timeoutToastIdRef = useRef(null);
  
  // Default loading state if none provided
  const defaultState = {
    text: 'This may take a few minutes, please wait...',
    progress: 100
  };

  const canActuallyGoToDesignPage = canGoToDesign && loadingFor == 'design';

  console.log('this is it ',canActuallyGoToDesignPage,loadingFor,canGoToDesign)

  // Move the switch statement logic into a useEffect to prevent infinite re-renders
  useEffect(() => {
    let message = '';
    switch(loadingFor) {
      case 'design':
        message = 'Your Book Design Is On The Way....';
        if(canActuallyGoToDesignPage){
          message = 'The Design is Partially Generated and Ready for Viewing....';
        }
        break;
      case 'story':
        message = 'A.I Story Generation Ongoing....';
        break;
      default:
        message = '';
    }
    setActualLoadingMessage(message);
  }, [loadingFor, canActuallyGoToDesignPage]);

  // Show timeout toast when canCancel becomes true; dismiss it again when the
  // process resumes making progress (canCancel goes back to false)
  useEffect(() => {
    if (canCancel && !timeoutToastIdRef.current && typeof onCancel === 'function') {
      timeoutToastIdRef.current = notify.processingTimeout(onCancel);
    } else if (!canCancel && timeoutToastIdRef.current) {
      toast.dismiss(timeoutToastIdRef.current);
      timeoutToastIdRef.current = null;
    }
  }, [canCancel, onCancel]);

  // Dismiss the timeout toast when the overlay unmounts (processing finished/cancelled)
  useEffect(() => {
    return () => {
      if (timeoutToastIdRef.current) {
        toast.dismiss(timeoutToastIdRef.current);
        timeoutToastIdRef.current = null;
      }
    };
  }, []);

  return (
    <>
      {/* Minimized Modal - Bottom Right Corner */}
      {isMinimized && (
        <div className="fixed bottom-6 right-6 w-96 bg-white rounded-lg shadow-2xl border border-gray-200 flex flex-col z-50 overflow-hidden">
          {/* Minimized Header */}
          <div className="flex justify-between items-center px-4 py-2 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-blue-50 flex-shrink-0">
            <h3 className="text-sm font-semibold text-gray-900 truncate">
              {loadingFor === 'design' ? 'Generating Design' : 'Generating Story Preview'}
            </h3>
            <button
              onClick={() => setIsMinimized(false)}
              className="text-gray-500 hover:text-gray-700 font-bold text-lg"
              aria-label="Maximize modal"
              title="Maximize"
            >
              ⛶
            </button>
          </div>

          {/* Minimized Progress */}
          <div className="px-4 py-2 flex flex-col justify-center flex-shrink-0">
            <div className="bg-gray-200 rounded-full h-1.5 w-full">
              <div
                className="bg-indigo-600 h-1.5 rounded-full transition-all duration-500"
                style={{ width: `${states.progress}%` || 0 }}
              />
            </div>
            <p className="text-xs font-medium text-gray-700 mt-1.5 text-center">{states.text}</p>
          </div>

          {/* Minimized Footer */}
          <div className="px-4 py-1.5 border-t border-gray-200 bg-gray-50 flex-shrink-0 flex items-center gap-2">
            <button
              onClick={() => setIsMinimized(false)}
              className="flex-1 text-xs font-medium text-indigo-600 hover:text-indigo-700 py-1"
            >
              View Full Details
            </button>
            {canCancel && typeof onCancel === 'function' && (
              <button
                onClick={onCancel}
                className="flex-1 text-xs font-medium text-red-600 hover:text-red-700 py-1"
                aria-label="Cancel the stuck generation process"
              >
                Cancel Process
              </button>
            )}
          </div>
        </div>
      )}

      {/* Full Modal - Original Layout */}
      {!isMinimized && (
        <div className="fixed inset-0 bg-white bg-opacity-90 flex items-center justify-center z-[60] overflow-hidden">
          {/* Minimize Button - Fixed to Modal Top Right */}
          <button
            onClick={() => setIsMinimized(true)}
            className="fixed top-6 right-6 px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-md transition-all duration-200 flex items-center gap-2 z-[70]"
            aria-label="Minimize modal"
            title="Minimize to corner"
          >
            <span>−</span>
            <span>Minimize</span>
          </button>

          {/* Expanded Preview Mode */}
          {expandedPreview && draftedResult && (
            <div className="fixed inset-0 bg-white bg-opacity-95 flex flex-col items-center justify-center z-50 p-6">
              {/* Close Button */}
              <button
                onClick={() => setExpandedPreview(false)}
                className="absolute top-6 right-6 flex items-center gap-2 px-4 py-2 bg-gray-600 text-white font-medium rounded hover:bg-gray-700 transition-colors"
                aria-label="Close expanded preview"
              >
                <span className="text-lg">✕</span>
                <span className="text-sm">Close</span>
              </button>

              {/* Expanded Preview Container */}
              <div className="w-full max-w-4xl h-full flex flex-col">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Generated Content Preview</h2>
                <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap text-sm leading-relaxed overflow-auto flex-1 mb-6 bg-gray-50 p-8 rounded-lg border border-gray-200">
                  {typeof draftedResult === 'string' ? draftedResult : JSON.stringify(draftedResult, null, 2)}
                </div>
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-md">
                  <p className="text-sm text-amber-900 leading-relaxed">
                    <span className="font-bold text-amber-700">⚠ Note:</span> This is an intermediate preview of your story outline. The final generated content may differ as additional processing and refinements are applied.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Main Container - Side by side or centered (hidden when preview is expanded) */}
          {!expandedPreview && (
            <div className={`flex ${(showGame || draftedResult) ? 'flex-row gap-4 justify-center' : 'w-full'} items-center h-full w-full`}>
        
              {/* Left Column: Loading UI - Fixed dimensions for consistency */}
              <div className="flex-1 px-4 py-6 flex flex-col justify-center items-center">
                {showTutorial && (
                  <div className="w-full max-w-5xl mb-4">
                    <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200 h-auto flex flex-col overflow-y-auto">
                      {/* Default tutorial depends on operation: preview -> Story (slide 2), design -> Character Consistency */}
                      <TutorialSlideshow
                        defaultTabId={loadingFor === 'preview' ? 2 : 7}
                        defaultSlideIndex={loadingFor === 'preview' ? 1 : 0}
                      />
                    </div>
                  </div>
                )}
                <div className="w-full max-w-2xl">
                  <div className="bg-gray-200 rounded-full h-2 w-full mt-2">
                    <div
                      className="bg-indigo-600 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${states.progress}%` || 0 }}
                    />
                  </div>
                  <p className="text-base font-medium text-gray-900 mt-3 text-center">{states.text}</p>
                  {(!canActuallyGoToDesignPage && actualLoadingMessage) && (
                    <div className="text-center mt-2">
                      <p className="text-sm font-medium text-gray-700">{actualLoadingMessage}</p>
                    </div>
                  )}
                </div>
                {canActuallyGoToDesignPage && (
                  <div className="mt-4">
                    <h3 className="text-center text-base font-semibold text-gray-900 mb-1">The Design is Partially Generated and Ready for Viewing…</h3>
                    <p className="text-center text-sm text-gray-600 mb-3">Refine characters, places, and items now, or jump straight into your layout.</p>
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center">
                      <button
                        aria-label="Continue to Design page to preview and arrange spreads"
                        className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded shadow"
                        onClick={() => {
                          setIsMinimized(true);
                          goToDesignRoute();
                        }}
                      >
                        Continue to Design
                      </button>
                      {typeof goToWorldRoute === 'function' && (
                        <button
                          aria-label="Explore World to review characters, places, and items"
                          className="border border-indigo-600 text-indigo-700 hover:bg-indigo-50 font-medium py-2 px-4 rounded"
                          onClick={() => {
                            setIsMinimized(true);
                            goToWorldRoute();
                          }}
                        >
                          Explore World
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

            {/* Right Column: Snake Game or Drafted Result - Conditional */}
            {showGame && (
              <div className="flex-1 px-4 py-6 flex justify-center">
                <SnakeGame onClose={() => setShowGame(false)} />
              </div>
            )}
            
            {draftedResult && !showGame && (
              <div className="flex-1 px-4 py-6 flex justify-center">
                <div className="w-full max-w-5xl bg-white rounded-lg shadow-md border border-gray-200 flex flex-col h-[750px]">
                  <div className="flex justify-between items-center mb-3 px-6 pt-6">
                    <h3 className="text-base font-semibold text-gray-900">Generated Content Preview</h3>
                    <button
                      onClick={() => setExpandedPreview(true)}
                      className="ml-2 px-3 py-1 bg-indigo-600 text-white text-xs font-medium rounded hover:bg-indigo-700 transition-colors"
                      aria-label="Expand preview to full screen"
                      title="Click to expand preview"
                    >
                      ⛶ Expand
                    </button>
                  </div>
                  <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap text-xs leading-relaxed overflow-auto flex-1 mb-3 px-6">
                    {typeof draftedResult === 'string' ? draftedResult : JSON.stringify(draftedResult, null, 2)}
                  </div>
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-md mx-6">
                    <p className="text-xs text-amber-900 leading-relaxed">
                      <span className="font-bold text-amber-700">⚠ Note:</span> This is an intermediate preview of your story outline. The final generated content may differ as additional processing and refinements are applied.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      )}
    </>
  );
}